/**
 * Visits analytics section.
 *
 * Reuses `GET /visits/analytics/trends` to show the approval status split and
 * the most-visited stores / most-active visitors.
 */

import React from 'react';
import {useTranslation} from 'react-i18next';
import {MapPinned} from 'lucide-react';
import AnalyticsSectionCard from '../AnalyticsSectionCard';
import MetricTile from '../MetricTile';
import MiniBarList from '../MiniBarList';
import StatusSplitBar from '../StatusSplitBar';
import {useVisitsAnalytics} from '../../hooks/useHomeAnalytics';
import type {RankedItem} from '../../models/homeAnalytics.model';
import type {MiniBarRow} from '../MiniBarList';

interface VisitsSectionProps {
  enabled: boolean;
}

/** Ranked entities render as `name` rows in the bar list. */
const toRows = (items: RankedItem[]): MiniBarRow[] =>
  items.map((item) => ({
    label: item.name,
    value: item.value,
    subtitle: item.subtitle
  }));

const VisitsSection: React.FC<VisitsSectionProps> = ({enabled}) => {
  const {t} = useTranslation();
  const {data, isLoading, isError} = useVisitsAnalytics(enabled);

  return (
    <AnalyticsSectionCard
      title={t('analytics.visits.title')}
      icon={<MapPinned className="w-5 h-5" />}
      isLoading={isLoading}
      isError={isError}
    >
      {data && (
        <div className="space-y-5">
          <MetricTile
            label={t('analytics.visits.total')}
            value={data.total}
            tone="primary"
          />

          <div>
            <h4 className="text-xs font-semibold text-gray-500 mb-2">
              {t('analytics.visits.status_split')}
            </h4>
            <StatusSplitBar
              segments={data.statusSplit}
              emptyLabel={t('analytics.no_data')}
            />
          </div>

          <div>
            <h4 className="text-xs font-semibold text-gray-500 mb-2">
              {t('analytics.visits.top_stores')}
            </h4>
            <MiniBarList
              rows={toRows(data.topStores)}
              tone="primary"
              ranked
              emptyLabel={t('analytics.no_data')}
            />
          </div>

          <div>
            <h4 className="text-xs font-semibold text-gray-500 mb-2">
              {t('analytics.visits.top_visitors')}
            </h4>
            <MiniBarList
              rows={toRows(data.topVisitors)}
              tone="success"
              ranked
              emptyLabel={t('analytics.no_data')}
            />
          </div>
        </div>
      )}
    </AnalyticsSectionCard>
  );
};

export default VisitsSection;
